import { searchNavbarAPI, searchInvestigationsAPI } from "../../apis";
import { ReportResults } from "./results/ReportResults";
import { InvestigationResults } from "./results/InvestigationResults";

/**
 * Registro de dominios del buscador.
 *
 * Cada dominio declara qué se puede buscar, con qué filtros y contra qué
 * endpoint. El input, los chips, el modal de filtros y los resultados leen de
 * aquí, así que agregar un dominio nuevo (usuarios, catálogos, bitácora) es
 * agregar una entrada, no tocar componentes.
 *
 * `fields`  -> chips de texto. El mismo término se manda en cada campo activo
 *              y el backend los combina con OR.
 * `filters` -> filtros del modal de búsqueda avanzada. `options` es la llave
 *              del catálogo que resuelve useFilterOptions().
 *
 * Un campo con `enabled: false` se pinta deshabilitado con el motivo que diga
 * `disabledReasonKey`. Hoy ninguno lo está.
 */

const FOLDER_HINT = "searchBar.field_folder_hint";

export const SEARCH_DOMAINS = {
  reports: {
    key: "reports",
    labelKey: "searchBar.domain_reports",
    search: searchNavbarAPI,
    extractResults: (resp) => resp?.results?.reports || [],
    Results: ReportResults,
    defaultFields: ["folio"],
    // Un solo campo a la vez: folio, seguimiento e involucrados son
    // identificadores distintos y buscarlos en paralelo desenfoca el resultado.
    singleField: true,
    // Los campos del formulario con `catalogue` se agregan como filtros al
    // vuelo; solo las denuncias tienen metadata de formulario.
    supportsCatalogues: true,
    fields: [
      { key: "folio", labelKey: "searchBar.filter_folio", enabled: true },
      {
        key: "tracking_code",
        labelKey: "searchBar.filter_tracking_code",
        enabled: true,
      },
      {
        key: "involved",
        labelKey: "searchBar.field_involved",
        enabled: true,
        hintKey: "searchBar.field_involved_hint",
      },
      // Campos propios del formulario que viven en metadata (nombre_completo
      // y soeid); el backend los busca por su key.
      {
        key: "name",
        labelKey: "searchBar.field_name",
        enabled: true,
        hintKey: "searchBar.field_name_hint",
      },
      {
        key: "soeid",
        labelKey: "searchBar.field_soeid",
        enabled: true,
        hintKey: "searchBar.field_soeid_hint",
      },
    ],
    filters: [
      {
        key: "status",
        labelKey: "Filter.status",
        options: "reportStatus",
        multiple: true,
      },
      {
        key: "severity",
        labelKey: "Filter.kind_of_severity",
        options: "severity",
        multiple: true,
      },
      {
        key: "indicators",
        labelKey: "ReportsList.indicators",
        options: "indicators",
        multiple: true,
      },
      {
        key: "entryway",
        labelKey: "Filter.kind_of_entryways",
        options: "entryways",
        multiple: true,
      },
      { key: "year", labelKey: "Filter.year", options: "years" },
    ],
  },

  investigations: {
    key: "investigations",
    labelKey: "searchBar.domain_investigations",
    search: searchInvestigationsAPI,
    extractResults: (resp) => resp?.results?.investigations || [],
    Results: InvestigationResults,
    defaultFields: ["alias"],
    // Un solo campo a la vez, igual que en denuncias: alias, entrevistas o
    // evidencias son preguntas distintas y mezclarlas con OR revuelve resultados
    // que no se parecen entre si.
    singleField: true,
    fields: [
      { key: "alias", labelKey: "searchBar.field_alias", enabled: true },
      {
        key: "report",
        labelKey: "searchBar.field_linked_report",
        enabled: true,
        hintKey: "searchBar.field_linked_report_hint",
      },
      { key: "title", labelKey: "searchBar.field_title", enabled: true },
      {
        key: "conclusion",
        labelKey: "searchBar.field_conclusion",
        enabled: true,
      },
      { key: "comment", labelKey: "searchBar.field_comment", enabled: true },
      // Secciones del folder. Solo alcanzan lo que esté guardado en claro: lo
      // que se cifró con KMS no lo encuentra ningún filtro, y no hay forma de
      // saberlo desde aquí, así que se ofrecen siempre y el aviso va en el
      // tooltip.
      {
        key: "involved",
        labelKey: "searchBar.field_involved",
        enabled: true,
        hintKey: FOLDER_HINT,
      },
      {
        key: "interviews",
        labelKey: "searchBar.field_interviews",
        enabled: true,
        hintKey: FOLDER_HINT,
      },
      {
        key: "evidences",
        labelKey: "searchBar.field_evidences",
        enabled: true,
        hintKey: FOLDER_HINT,
      },
      {
        key: "plans",
        labelKey: "searchBar.field_plans",
        enabled: true,
        hintKey: FOLDER_HINT,
      },
    ],
    filters: [
      {
        key: "status",
        labelKey: "Filter.status",
        options: "investigationStatus",
        multiple: true,
      },
      { key: "year", labelKey: "Filter.year", options: "years" },
      {
        key: "advance",
        labelKey: "searchBar.filter_investigation_kind",
        options: "investigationKind",
      },
    ],
  },
};

export const DEFAULT_DOMAIN = "reports";

export const DOMAIN_KEYS = Object.keys(SEARCH_DOMAINS);

export const getDomain = (key) => SEARCH_DOMAINS[key] || SEARCH_DOMAINS[DEFAULT_DOMAIN];

/** Campos que hoy sí pueden consultarse en un dominio */
export const getEnabledFields = (domain) =>
  domain.fields.filter((field) => field.enabled).map((field) => field.key);

/**
 * Dominio + los filtros que se descubren en tiempo de ejecución (los catálogos
 * del formulario). Se resuelve aquí para que todo lo demás siga leyendo una
 * sola lista de filtros.
 */
export const withDynamicFilters = (domain, dynamicFilters = []) => {
  if (!domain.supportsCatalogues || dynamicFilters.length === 0) return domain;

  return { ...domain, filters: [...domain.filters, ...dynamicFilters] };
};
